import React, { useState } from 'react';
import { 
  Search, 
  ArrowRight, 
  CheckCircle2, 
  Sliders, 
  Globe, 
  Lock, 
  ArrowLeftRight, 
  TrendingUp, 
  Calculator, 
  Landmark, 
  Sparkles, 
  Receipt,
  FileText,
  ChevronRight,
  ExternalLink,
  DollarSign
} from 'lucide-react';
import { AppView, RegionInfo, ToolItem } from '../../types';
import { LIVE_EXCHANGE_RATES } from '../../data/regionsData';
import { MethodologyDrawer } from '../design-system/MethodologyDrawer';

interface HomepageViewProps {
  onNavigate: (view: AppView) => void;
  onSelectTool: (toolId: string) => void;
  onOpenSearch: () => void;
  selectedRegion: RegionInfo;
  onSelectRegion: (reg: RegionInfo) => void;
  allRegions: RegionInfo[];
  tools: ToolItem[];
  onSaveCalculation?: (toolId: string, primaryResult: string, summary: string) => void;
}

export const HomepageView: React.FC<HomepageViewProps> = ({
  onNavigate,
  onSelectTool,
  onOpenSearch,
  selectedRegion,
  onSelectRegion,
  allRegions,
  tools,
}) => {
  // Search query state inside the hero input
  const [heroSearchQuery, setHeroSearchQuery] = useState('');

  // Featured Converter State
  const [fxAmount, setFxAmount] = useState<number>(1000);
  const [fxFrom, setFxFrom] = useState<string>('USD');
  const [fxTo, setFxTo] = useState<string>('EUR');

  // Methodology Modal State
  const [methodologyOpen, setMethodologyOpen] = useState(false);

  // Exchange calculations
  const fromRate = LIVE_EXCHANGE_RATES[fxFrom] || 1.0;
  const toRate = LIVE_EXCHANGE_RATES[fxTo] || 0.9234;
  const unitRate = toRate / fromRate;
  const convertedTotal = (fxAmount * unitRate).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const handleSwapCurrencies = () => {
    setFxFrom(fxTo);
    setFxTo(fxFrom);
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onOpenSearch();
  };

  return (
    <div className="space-y-16 lg:space-y-20 pb-20 text-[#1A1A1A]">
      {/* 01. Discreet Location & Jurisdiction Detection Banner */}
      <div className="bg-[#F9FAFB] border-b border-[#E5E7EB] py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2 text-[#4B5563]">
            <span className="text-sm">{selectedRegion.flag}</span>
            <span>
              Active jurisdiction: <strong className="text-[#1A1A1A] font-semibold">{selectedRegion.name}</strong> ({selectedRegion.currency}, {selectedRegion.taxYear} Ruleset)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[#6B7280]">Change region:</span>
            <select
              value={selectedRegion.code}
              onChange={(e) => {
                const reg = allRegions.find((r) => r.code === e.target.value);
                if (reg) onSelectRegion(reg);
              }}
              className="bg-white border border-[#E5E7EB] text-[#1A1A1A] font-medium text-xs px-2 py-1 rounded-md outline-none cursor-pointer hover:border-[#1A1A1A] transition-colors"
            >
              {allRegions.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.flag} {r.name} ({r.currency})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 02. HERO SECTION */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6 pt-4 sm:pt-8">
        <div className="space-y-4">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A1A1A] tracking-tight leading-[1.15]">
            Smarter money calculations. <br className="hidden sm:inline" />
            <span className="text-[#4B5563]">Wherever you live.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#6B7280] max-w-2xl mx-auto leading-relaxed">
            Precision tools for everyday financial decisions. Built on verified data and local jurisdiction rules.
          </p>
        </div>

        {/* Universal Search Bar */}
        <div className="max-w-2xl mx-auto pt-2">
          <form onSubmit={handleSearchSubmit} className="relative flex items-center">
            <div className="relative w-full flex items-center">
              <Search className="absolute left-4 w-5 h-5 text-[#9CA3AF] pointer-events-none" />
              <input
                type="text"
                value={heroSearchQuery}
                onChange={(e) => setHeroSearchQuery(e.target.value)}
                onClick={onOpenSearch}
                placeholder="Search for tools, currency, or tax calculators..."
                className="w-full pl-12 pr-28 py-3.5 sm:py-4 bg-white border border-[#D1D5DB] focus:border-[#1A1A1A] focus:ring-1 focus:ring-[#1A1A1A] rounded-xl text-sm sm:text-base text-[#1A1A1A] placeholder-[#9CA3AF] shadow-xs outline-none transition-all cursor-pointer"
              />
              <button
                type="button"
                onClick={onOpenSearch}
                className="absolute right-2 px-4 sm:px-5 py-2 bg-[#1A1A1A] hover:bg-black text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors cursor-pointer"
              >
                Search
              </button>
            </div>
          </form>

          {/* Quick Shortcuts */}
          <div className="flex items-center justify-center gap-2 pt-3 flex-wrap text-xs text-[#6B7280]">
            <span className="font-medium">Popular:</span>
            {[
              { label: 'USD to EUR', id: 'currency-converter' },
              { label: 'Net Paycheck', id: 'salary-calculator' },
              { label: 'Mortgage Repayment', id: 'mortgage-calculator' },
              { label: 'Compare Transfers', id: 'compare-transfers' },
            ].map((shortcut) => (
              <button
                key={shortcut.id}
                onClick={() => onSelectTool(shortcut.id)}
                className="px-2.5 py-1 bg-white hover:bg-[#F3F4F6] border border-[#E5E7EB] text-[#4B5563] hover:text-[#1A1A1A] rounded-md transition-colors cursor-pointer"
              >
                {shortcut.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* 03. FEATURED CALCULATOR: LIVE CURRENCY CONVERTER */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sm:p-8 shadow-xs space-y-6">
          <div className="flex items-center justify-between border-b border-[#F3F4F6] pb-4">
            <h2 className="text-lg font-bold text-[#1A1A1A]">
              Currency Converter
            </h2>
            <div className="flex items-center gap-1.5 text-xs text-[#0E7044] font-medium bg-[#F0FDF4] px-2.5 py-1 rounded-full border border-[#DCFCE7]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse"></span>
              <span>Live Mid-Market Rates</span>
            </div>
          </div>

          {/* Form Row: Amount | From | Swap | To */}
          <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-4 items-end">
            {/* Amount */}
            <div className="sm:col-span-4 space-y-1.5 text-left">
              <label className="block text-xs font-semibold text-[#6B7280]">
                Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#9CA3AF]">
                  $
                </span>
                <input
                  type="number"
                  min="1"
                  step="any"
                  value={fxAmount || ''}
                  onChange={(e) => setFxAmount(parseFloat(e.target.value) || 0)}
                  className="w-full pl-8 pr-3 py-2.5 bg-[#FAFAFA] border border-[#E5E7EB] focus:bg-white focus:border-[#1A1A1A] rounded-xl text-sm font-semibold text-[#1A1A1A] outline-none transition-colors"
                />
              </div>
            </div>

            {/* From */}
            <div className="sm:col-span-3 space-y-1.5 text-left">
              <label className="block text-xs font-semibold text-[#6B7280]">
                From
              </label>
              <select
                value={fxFrom}
                onChange={(e) => setFxFrom(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#FAFAFA] border border-[#E5E7EB] focus:bg-white focus:border-[#1A1A1A] rounded-xl text-sm font-semibold text-[#1A1A1A] outline-none transition-colors cursor-pointer"
              >
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="JPY">JPY - Japanese Yen</option>
                <option value="AUD">AUD - Australian Dollar</option>
                <option value="CHF">CHF - Swiss Franc</option>
                <option value="INR">INR - Indian Rupee</option>
                <option value="XAF">XAF - Central African CFA</option>
              </select>
            </div>

            {/* Swap Button */}
            <div className="sm:col-span-2 flex items-center justify-center pb-0.5">
              <button
                type="button"
                onClick={handleSwapCurrencies}
                aria-label="Swap currencies"
                className="p-2.5 bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#1A1A1A] rounded-xl transition-colors cursor-pointer border border-[#E5E7EB]"
                title="Swap currencies"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
            </div>

            {/* To */}
            <div className="sm:col-span-3 space-y-1.5 text-left">
              <label className="block text-xs font-semibold text-[#6B7280]">
                To
              </label>
              <select
                value={fxTo}
                onChange={(e) => setFxTo(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#FAFAFA] border border-[#E5E7EB] focus:bg-white focus:border-[#1A1A1A] rounded-xl text-sm font-semibold text-[#1A1A1A] outline-none transition-colors cursor-pointer"
              >
                <option value="EUR">EUR - Euro</option>
                <option value="USD">USD - US Dollar</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="JPY">JPY - Japanese Yen</option>
                <option value="AUD">AUD - Australian Dollar</option>
                <option value="CHF">CHF - Swiss Franc</option>
                <option value="INR">INR - Indian Rupee</option>
                <option value="XAF">XAF - Central African CFA</option>
              </select>
            </div>
          </div>

          {/* Results Summary Row */}
          <div className="pt-4 border-t border-[#F3F4F6] flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1 text-left">
              <div className="text-xs font-medium text-[#6B7280]">
                {fxAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} {fxFrom} =
              </div>
              <div className="text-2xl sm:text-3xl font-extrabold text-[#1A1A1A] tracking-tight">
                {convertedTotal} <span className="text-xl font-bold text-[#4B5563]">{fxTo}</span>
              </div>
              <div className="flex items-center gap-2 pt-1 text-[11px] text-[#9CA3AF]">
                <span>Mid-market rate: 1 {fxFrom} = {unitRate.toFixed(4)} {fxTo}</span>
                <span>•</span>
                <span>Updated 2 mins ago</span>
                <span>•</span>
                <button
                  onClick={() => setMethodologyOpen(true)}
                  className="underline hover:text-[#1A1A1A] cursor-pointer"
                >
                  Methodology
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => onNavigate('money-transfers')}
                className="w-full sm:w-auto px-5 py-2.5 bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#1D4ED8] font-semibold text-xs rounded-xl border border-[#BFDBFE] transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <span>View Transfer Options</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* 04. POPULAR CALCULATORS & LIVE RATES SNAPSHOT */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column: Popular Calculators (3 Cards Grid) */}
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-[#1A1A1A]">
                Popular Calculators
              </h2>
              <button
                onClick={() => onNavigate('tools')}
                className="text-xs font-semibold text-[#4B5563] hover:text-[#1A1A1A] flex items-center gap-1 cursor-pointer"
              >
                <span>View all tools</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Card 1: Calculate Net Pay */}
              <button
                onClick={() => onNavigate('salary-engine')}
                className="p-4 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl text-left transition-all space-y-2 group shadow-2xs cursor-pointer flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A] group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
                    <Calculator className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xs text-[#1A1A1A] group-hover:text-[#0E7044] transition-colors">
                      Calculate Net Pay
                    </h3>
                    <p className="text-[11px] text-[#6B7280] leading-snug mt-1">
                      Estimate take-home pay after taxes and deductions.
                    </p>
                  </div>
                </div>
                <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                  <span>Calculate</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              </button>

              {/* Card 2: Compare Rates */}
              <button
                onClick={() => onNavigate('money-transfers')}
                className="p-4 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl text-left transition-all space-y-2 group shadow-2xs cursor-pointer flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A] group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
                    <ArrowLeftRight className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xs text-[#1A1A1A] group-hover:text-[#0E7044] transition-colors">
                      Compare Rates
                    </h3>
                    <p className="text-[11px] text-[#6B7280] leading-snug mt-1">
                      Find the best exchange rates across providers.
                    </p>
                  </div>
                </div>
                <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                  <span>Compare</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              </button>

              {/* Card 3: Estimate Mortgage */}
              <button
                onClick={() => onNavigate('loan-engine')}
                className="p-4 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl text-left transition-all space-y-2 group shadow-2xs cursor-pointer flex flex-col justify-between"
              >
                <div className="space-y-2">
                  <div className="w-8 h-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A] group-hover:bg-[#1A1A1A] group-hover:text-white transition-colors">
                    <Landmark className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xs text-[#1A1A1A] group-hover:text-[#0E7044] transition-colors">
                      Estimate Mortgage
                    </h3>
                    <p className="text-[11px] text-[#6B7280] leading-snug mt-1">
                      Calculate monthly payments and interest rates.
                    </p>
                  </div>
                </div>
                <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                  <span>Estimate</span>
                  <ArrowRight className="w-3 h-3" />
                </div>
              </button>
            </div>
          </div>

          {/* Right Column: Live Rates Snapshot */}
          <div className="lg:col-span-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-[#1A1A1A]">
                Live Rates Snapshot
              </h2>
              <button
                onClick={() => onNavigate('currency-intelligence')}
                className="text-xs font-semibold text-[#4B5563] hover:text-[#1A1A1A] flex items-center gap-1 cursor-pointer"
              >
                <span>Full board</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="bg-white border border-[#E5E7EB] rounded-xl p-3.5 space-y-2.5 shadow-2xs">
              {/* Pair 1: EUR / USD */}
              <div 
                onClick={() => onNavigate('currency-intelligence')}
                className="p-3 bg-[#FBFBFC] hover:bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg flex items-center justify-between transition-colors cursor-pointer"
              >
                <div className="space-y-0.5">
                  <div className="font-bold text-xs text-[#1A1A1A]">EUR / USD</div>
                  <div className="text-[11px] text-[#6B7280]">Euro / US Dollar</div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Sparkline chart SVG */}
                  <svg className="w-16 h-6 text-emerald-600 overflow-visible" viewBox="0 0 60 20">
                    <polyline
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      points="0,15 10,13 20,16 30,10 40,8 50,11 60,4"
                    />
                  </svg>
                  <div className="text-right">
                    <div className="font-mono font-bold text-xs text-[#1A1A1A]">1.0815</div>
                    <div className="text-[10px] font-bold text-emerald-600">+0.12%</div>
                  </div>
                </div>
              </div>

              {/* Pair 2: GBP / USD */}
              <div 
                onClick={() => onNavigate('currency-intelligence')}
                className="p-3 bg-[#FBFBFC] hover:bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg flex items-center justify-between transition-colors cursor-pointer"
              >
                <div className="space-y-0.5">
                  <div className="font-bold text-xs text-[#1A1A1A]">GBP / USD</div>
                  <div className="text-[11px] text-[#6B7280]">British Pound / US Dollar</div>
                </div>
                <div className="flex items-center gap-3">
                  {/* Sparkline chart SVG */}
                  <svg className="w-16 h-6 text-[#6B7280] overflow-visible" viewBox="0 0 60 20">
                    <polyline
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      points="0,6 10,8 20,5 30,12 40,11 50,15 60,14"
                    />
                  </svg>
                  <div className="text-right">
                    <div className="font-mono font-bold text-xs text-[#1A1A1A]">1.2640</div>
                    <div className="text-[10px] font-bold text-[#6B7280]">-0.05%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 05. EXPLORE BY FINANCIAL NEED (5 Categories) */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 space-y-4">
        <div className="border-b border-[#E5E7EB] pb-3">
          <h2 className="text-lg font-bold text-[#1A1A1A]">
            Explore by Financial Need
          </h2>
          <p className="text-xs text-[#6B7280]">
            Select your goal to find specialized, jurisdiction-aware tools
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          {/* 1. Everyday Money */}
          <div 
            onClick={() => onNavigate('everyday-command-center')}
            className="p-4 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl text-left transition-all space-y-2 group shadow-2xs cursor-pointer flex flex-col justify-between"
          >
            <div className="space-y-2">
              <div className="w-8 h-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
                <Receipt className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-xs text-[#1A1A1A]">
                  Everyday Money
                </h3>
                <p className="text-[11px] text-[#6B7280] leading-snug mt-1">
                  Simple tools for daily expenses.
                </p>
              </div>
            </div>
            <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              <span>Explore</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </div>

          {/* 2. Currency & Transfers */}
          <div 
            onClick={() => onNavigate('currency-intelligence')}
            className="p-4 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl text-left transition-all space-y-2 group shadow-2xs cursor-pointer flex flex-col justify-between"
          >
            <div className="space-y-2">
              <div className="w-8 h-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
                <TrendingUp className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-xs text-[#1A1A1A]">
                  Currency & Transfers
                </h3>
                <p className="text-[11px] text-[#6B7280] leading-snug mt-1">
                  Global exchange intelligence.
                </p>
              </div>
            </div>
            <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              <span>Explore</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </div>

          {/* 3. Income & Tax */}
          <div 
            onClick={() => onNavigate('salary-engine')}
            className="p-4 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl text-left transition-all space-y-2 group shadow-2xs cursor-pointer flex flex-col justify-between"
          >
            <div className="space-y-2">
              <div className="w-8 h-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
                <Calculator className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-xs text-[#1A1A1A]">
                  Income & Tax
                </h3>
                <p className="text-[11px] text-[#6B7280] leading-snug mt-1">
                  Know your net worth.
                </p>
              </div>
            </div>
            <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              <span>Explore</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </div>

          {/* 4. Borrow */}
          <div 
            onClick={() => onNavigate('loan-engine')}
            className="p-4 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl text-left transition-all space-y-2 group shadow-2xs cursor-pointer flex flex-col justify-between"
          >
            <div className="space-y-2">
              <div className="w-8 h-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
                <Landmark className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-xs text-[#1A1A1A]">
                  Borrow
                </h3>
                <p className="text-[11px] text-[#6B7280] leading-snug mt-1">
                  Plan your financing.
                </p>
              </div>
            </div>
            <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              <span>Explore</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </div>

          {/* 5. Save & Invest */}
          <div 
            onClick={() => onNavigate('wealth-command-center')}
            className="p-4 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl text-left transition-all space-y-2 group shadow-2xs cursor-pointer flex flex-col justify-between col-span-2 sm:col-span-1"
          >
            <div className="space-y-2">
              <div className="w-8 h-8 rounded-lg bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-bold text-xs text-[#1A1A1A]">
                  Save & Invest
                </h3>
                <p className="text-[11px] text-[#6B7280] leading-snug mt-1">
                  Future wealth scenarios.
                </p>
              </div>
            </div>
            <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              <span>Explore</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </div>
        </div>
      </section>

      {/* 06. TRUST SECTION: BUILT FOR FINANCIAL CLARITY */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 space-y-6">
        <div className="text-center space-y-1">
          <h2 className="text-xl font-bold text-[#1A1A1A]">
            Built for Financial Clarity
          </h2>
          <p className="text-xs text-[#6B7280]">
            Transparent, verifiable, and private financial mathematics
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Principle 1 */}
          <div className="p-5 bg-white border border-[#E5E7EB] rounded-xl space-y-2 text-center shadow-2xs">
            <div className="w-10 h-10 mx-auto rounded-full bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
              <CheckCircle2 className="w-5 h-5 text-[#0E7044]" />
            </div>
            <h3 className="font-bold text-xs text-[#1A1A1A]">
              Verified Data Sources
            </h3>
            <p className="text-[11px] text-[#6B7280] leading-relaxed">
              Calculations built on verified global financial indices.
            </p>
          </div>

          {/* Principle 2 */}
          <div className="p-5 bg-white border border-[#E5E7EB] rounded-xl space-y-2 text-center shadow-2xs">
            <div className="w-10 h-10 mx-auto rounded-full bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
              <Sliders className="w-5 h-5 text-[#1A1A1A]" />
            </div>
            <h3 className="font-bold text-xs text-[#1A1A1A]">
              Transparent Methodology
            </h3>
            <p className="text-[11px] text-[#6B7280] leading-relaxed">
              Open-math approaches for every result.
            </p>
          </div>

          {/* Principle 3 */}
          <div className="p-5 bg-white border border-[#E5E7EB] rounded-xl space-y-2 text-center shadow-2xs">
            <div className="w-10 h-10 mx-auto rounded-full bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
              <Globe className="w-5 h-5 text-[#1A1A1A]" />
            </div>
            <h3 className="font-bold text-xs text-[#1A1A1A]">
              Jurisdiction-Aware Rules
            </h3>
            <p className="text-[11px] text-[#6B7280] leading-relaxed">
              Tax and financial laws mapped to your region.
            </p>
          </div>

          {/* Principle 4 */}
          <div className="p-5 bg-white border border-[#E5E7EB] rounded-xl space-y-2 text-center shadow-2xs">
            <div className="w-10 h-10 mx-auto rounded-full bg-[#F3F4F6] flex items-center justify-center text-[#1A1A1A]">
              <Lock className="w-5 h-5 text-[#1A1A1A]" />
            </div>
            <h3 className="font-bold text-xs text-[#1A1A1A]">
              Privacy by Design
            </h3>
            <p className="text-[11px] text-[#6B7280] leading-relaxed">
              Your data stays in your head, not our database.
            </p>
          </div>
        </div>
      </section>

      {/* 07. SELECTED INSIGHTS (3 Editorial Articles) */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 space-y-4">
        <div className="flex items-center justify-between border-b border-[#E5E7EB] pb-3">
          <div>
            <h2 className="text-lg font-bold text-[#1A1A1A]">
              Selected Insights
            </h2>
            <p className="text-xs text-[#6B7280]">
              Expert analysis on global tax rules, lending trends, and cross-border money
            </p>
          </div>
          <button
            onClick={() => onNavigate('financial-guides')}
            className="text-xs font-semibold text-[#4B5563] hover:text-[#1A1A1A] flex items-center gap-1 cursor-pointer"
          >
            <span>View all guides</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Article 1: Understanding Global VAT Changes */}
          <div 
            onClick={() => onNavigate('financial-guides')}
            className="bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl p-4 space-y-3 shadow-2xs transition-all cursor-pointer flex flex-col justify-between group"
          >
            <div className="space-y-3">
              <div className="h-32 bg-[#F3F4F6] rounded-lg flex items-center justify-center text-[#9CA3AF] group-hover:bg-[#E5E7EB] transition-colors">
                <FileText className="w-8 h-8" />
              </div>
              <div className="space-y-1">
                <h3 className="font-bold text-xs sm:text-sm text-[#1A1A1A] group-hover:text-[#0E7044] transition-colors">
                  Understanding Global VAT Changes
                </h3>
                <p className="text-[11px] text-[#6B7280] leading-relaxed">
                  A comprehensive guide to value-added tax adjustments across major economies.
                </p>
              </div>
            </div>
            <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              <span>Read Guide</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </div>

          {/* Article 2: Mortgage rates deep dive */}
          <div 
            onClick={() => onNavigate('financial-guides')}
            className="bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl p-4 space-y-3 shadow-2xs transition-all cursor-pointer flex flex-col justify-between group"
          >
            <div className="space-y-3">
              <div className="h-32 bg-[#F3F4F6] rounded-lg flex items-center justify-center text-[#9CA3AF] group-hover:bg-[#E5E7EB] transition-colors">
                <FileText className="w-8 h-8" />
              </div>
              <div className="space-y-1">
                <h3 className="font-bold text-xs sm:text-sm text-[#1A1A1A] group-hover:text-[#0E7044] transition-colors">
                  Mortgage rates deep dive
                </h3>
                <p className="text-[11px] text-[#6B7280] leading-relaxed">
                  Analyzing current trends in global lending and what it means for buyers.
                </p>
              </div>
            </div>
            <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              <span>Read Guide</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </div>

          {/* Article 3: The Future of Digital Payments */}
          <div 
            onClick={() => onNavigate('financial-guides')}
            className="bg-white border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-xl p-4 space-y-3 shadow-2xs transition-all cursor-pointer flex flex-col justify-between group"
          >
            <div className="space-y-3">
              <div className="h-32 bg-[#F3F4F6] rounded-lg flex items-center justify-center text-[#9CA3AF] group-hover:bg-[#E5E7EB] transition-colors">
                <FileText className="w-8 h-8" />
              </div>
              <div className="space-y-1">
                <h3 className="font-bold text-xs sm:text-sm text-[#1A1A1A] group-hover:text-[#0E7044] transition-colors">
                  The Future of Digital Payments
                </h3>
                <p className="text-[11px] text-[#6B7280] leading-relaxed">
                  How cross-border transactions are evolving for the modern workforce.
                </p>
              </div>
            </div>
            <div className="pt-2 text-[11px] font-semibold text-[#1A1A1A] flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              <span>Read Guide</span>
              <ArrowRight className="w-3 h-3" />
            </div>
          </div>
        </div>
      </section>

      {/* 08. DEVELOPERS SECTION (Pale Teal Banner) */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="bg-[#E6F7F2] border border-[#A7EBD5] rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl text-left">
            <h2 className="text-xl font-extrabold text-[#064E3B] tracking-tight">
              Build with YamooPay
            </h2>
            <p className="text-xs sm:text-sm text-[#047857] leading-relaxed">
              Financial calculations and money intelligence for your applications. Integrate our verified logic directly into your stack.
            </p>
          </div>
          <button
            onClick={() => onNavigate('developer-portal')}
            className="px-5 py-2.5 bg-white hover:bg-[#F0FDF4] text-[#064E3B] border border-[#A7EBD5] rounded-xl text-xs font-bold transition-colors shadow-2xs whitespace-nowrap cursor-pointer flex items-center gap-1.5"
          >
            <span>Explore Developer Platform</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </section>

      {/* 09. FINAL CALL TO ACTION */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-6 pt-4">
        <div className="space-y-2">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1A1A1A] tracking-tight">
            Make your next money decision clearer.
          </h2>
          <p className="text-xs sm:text-sm text-[#6B7280]">
            Start calculating immediately or create a private workspace to save scenarios.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => onNavigate('tools')}
            className="w-full sm:w-auto px-6 py-3 bg-[#1A1A1A] hover:bg-black text-white text-xs sm:text-sm font-semibold rounded-xl transition-colors shadow-xs cursor-pointer"
          >
            Explore tools
          </button>
          <button
            onClick={() => onNavigate('my-yamoopay')}
            className="w-full sm:w-auto px-6 py-3 bg-white hover:bg-[#F9FAFB] border border-[#E5E7EB] text-[#1A1A1A] text-xs sm:text-sm font-semibold rounded-xl transition-colors shadow-2xs cursor-pointer"
          >
            Create free workspace
          </button>
        </div>
      </section>

      {/* Methodology Drawer Modal */}
      <MethodologyDrawer
        isOpen={methodologyOpen}
        onClose={() => setMethodologyOpen(false)}
        toolName="Currency Converter & FX Intelligence"
        source="ECB, US Federal Reserve & Multi-Source Institutional Interbank Feed"
        formulaExplanation="Direct wholesale mid-market rate calculation with reciprocal triangulation: Quote / Base with 10^-8 decimal arithmetic."
        jurisdictionDate="Updated continuously (2-minute cache interval)"
        assumptions={[
          'Mid-market rate represents the midpoint between buy and sell prices in global interbank trade.',
          'Excludes retail bank spread markups (typically 1.5% - 3.5%) and fixed wire fees.',
          'Provides neutral benchmark intelligence before executing transfers.',
        ]}
      />
    </div>
  );
};
